import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  DialogActions,
  Button,
  Grid,
  Typography,
  CircularProgress,
  useMediaQuery,
  Box,
  MenuItem,
  Select,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

const REPORT_LANGUAGES = [
  { value: "fr", label: "French" },
  { value: "eng", label: "English" },
  { value: "esp", label: "Spanish" },
];

const ConfirmEvaluationModal = ({
  open,
  onClose,
  onConfirm,
  loading,
  creditLoading = false,
  creditSummary = null,
  reportLanguage = "fr",
  onReportLanguageChange,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:1400px)");
  const initialCredits = Number(
    creditSummary?.initialCredits ?? creditSummary?.availableBefore ?? 0
  );
  const reserveCost = Number(creditSummary?.reserveCost ?? 0);
  const remainingAfterReport = Number(
    creditSummary?.remainingAfterReport ??
      creditSummary?.availableAfter ??
      Math.max(initialCredits - reserveCost, 0)
  );
  const hasInsufficientCredits =
    creditSummary && reserveCost > 0 && initialCredits < reserveCost;
  const missingCredits = hasInsufficientCredits
    ? Math.max(reserveCost - initialCredits, 0)
    : 0;
  const showReportSettings = Boolean(creditSummary && !hasInsufficientCredits);
  const creditTableRows = [
    {
      key: "initial",
      label: t("available_credits", { defaultValue: "Available Credits" }),
      value: initialCredits,
    },
    {
      key: "reserve",
      label: t("credits_to_reserve", { defaultValue: "Credits To Reserve" }),
      value: reserveCost,
    },
    ...(!hasInsufficientCredits
      ? [
          {
            key: "remaining",
            label: t("remaining_after_reports_generation", {
              defaultValue: "Remaining Credits After Report Generation",
            }),
            value: remainingAfterReport,
          },
        ]
      : []),
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      PaperProps={{
        sx: !isMobile
          ? { width: "30%", padding: "20px" }
          : {
              width: "90%",
              margin: "5px 0",
              padding: "5px",
            },
      }}
      disableBackdropClick
      disableEnforceFocus
    >
      

      <DialogTitle mb={1} align="center">
        <Typography
          variant="title1"
          sx={{
            fontSize: { xs: "26px", sm: "30px" },
            lineHeight: 1.2,
            fontWeight: 700,
          }}
        >
          {t("evaluation_authorization_required", {
            defaultValue: "Evaluation Authorization Required",
          })}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ padding: "15px 10px" }}>
        <DialogContentText mb={2} align="center" sx={{ fontSize: "1.05rem" }}>
          {t("evaluation_authorization_message_line_1", {
            defaultValue:
              "This action will initiate the evaluation of the selected organization.",
          })}
        </DialogContentText>
        <DialogContentText mb={2} align="center" sx={{ fontSize: "1.05rem" }}>
          {t("evaluation_authorization_message_line_2", {
            defaultValue:
              "Please confirm that you hold the required Active Scan Authorization signed by the legal representative of the domain owner.",
          })}
        </DialogContentText>
        {creditSummary && (
          <Box
            sx={{
              mt: 1.5,
              p: 0,
              border: "none",
              backgroundColor: "transparent",
              borderRadius: 0,
            }}
          >
            <Box
              sx={{
                borderRadius: "10px",
                border: "1px solid #DDE7E4",
                backgroundColor: "#FFFFFF",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  columnGap: 1,
                  px: 1.4,
                  py: 0.95,
                  backgroundColor: "#F3F8F6",
                  borderBottom: "1px solid #E6EEEB",
                }}
              >
                <Typography variant="subtitle7" sx={{ color: "#475569", fontWeight: 700 }}>
                  {t("credit_plan_information", { defaultValue: "Credit Plan Information" })}
                </Typography>
                <Typography
                  variant="subtitle7"
                  sx={{ color: "#475569", fontWeight: 700, textAlign: "right" }}
                >
                  {t("amount", { defaultValue: "Amount" })}
                </Typography>
              </Box>
              {creditTableRows.map((row, index) => (
                <Box
                  key={row.key}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    columnGap: 1,
                    px: 1.4,
                    py: 1,
                    borderBottom:
                      index !== creditTableRows.length - 1 ? "1px solid #EDF2F0" : "none",
                  }}
                >
                  <Typography variant="subtitle7" sx={{ color: "#334155" }}>
                    {row.label}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#0F172A",
                      lineHeight: 1.2,
                      textAlign: "right",
                      minWidth: "32px",
                    }}
                  >
                    {row.value}
                  </Typography>
                </Box>
              ))}
            </Box>

            {hasInsufficientCredits && (
              <Box
                sx={{
                  mt: 1.1,
                  p: 1,
                  borderRadius: "10px",
                  border: "1px solid #F59E0B",
                  backgroundColor: "#FFF7ED",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 0.8,
                }}
              >
                <WarningAmberRoundedIcon sx={{ color: "#EA580C", fontSize: 18, mt: "2px" }} />
                <Typography variant="subtitle7" sx={{ color: "#7C2D12", lineHeight: 1.5 }}>
                  Not enough available credits for this action. You need{" "}
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {missingCredits}
                  </Box>{" "}
                  more credits. Please contact admin@cytailor.io.
                </Typography>
              </Box>
            )}

            {showReportSettings && (
              <Box
                sx={{
                  mt: 1.4,
                  borderRadius: "10px",
                  border: "1px solid #DDE7E4",
                  backgroundColor: "#FFFFFF",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    px: 1.4,
                    py: 0.95,
                    backgroundColor: "#F3F8F6",
                    borderBottom: "1px solid #E6EEEB",
                  }}
                >
                  <Typography variant="subtitle7" sx={{ color: "#475569", fontWeight: 700 }}>
                    {t("report_settings", { defaultValue: "Report Settings" })}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 180px" },
                    alignItems: "center",
                    gap: 1,
                    px: 1.4,
                    py: 1.1,
                  }}
                >
                  <Typography variant="subtitle7" sx={{ color: "#334155" }}>
                    {t("report_language", { defaultValue: "Report Language" })}
                  </Typography>
                  <Select
                    value={reportLanguage}
                    onChange={(event) => onReportLanguageChange?.(event.target.value)}
                    disabled={loading || creditLoading}
                    size="small"
                    fullWidth
                    sx={{
                      height: 36,
                      borderRadius: "8px",
                      color: "#0F172A",
                      backgroundColor: "#FFFFFF",
                      ".MuiOutlinedInput-notchedOutline": {
                        borderColor: "#CBD5E1",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#78C3AE",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00A886",
                      },
                    }}
                  >
                    {REPORT_LANGUAGES.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {t(`report_language_${option.value}`, {
                          defaultValue: option.label,
                        })}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: "20px" }}>
        <Grid container spacing={5}>
          <Grid item xs={6}>
            <Button
              variant="contained"
              color="deleteButton"
              onClick={onClose}
              fullWidth
              disabled={loading || creditLoading}
            >
              {t("cancel")}
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              variant="contained"
              color="button"
              onClick={onConfirm}
              fullWidth
              disabled={loading || creditLoading || hasInsufficientCredits}
              startIcon={loading || creditLoading ? <CircularProgress size={24} /> : null}
            >
              {t("confirm")}
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmEvaluationModal;
